/**
 * Select/Dropdown Component
 *
 * Accessible select dropdown with custom styling.
 * Supports single/multi-select, search, groups, and custom rendering.
 *
 * Usage:
 * <Select
 *   options={[{ value: 'a', label: 'Option A' }]}
 *   value={selected}
 *   onChange={setSelected}
 * />
 */

import React, { useState, useRef, useEffect, useId, useCallback } from 'react';
import PropTypes from 'prop-types';

// ========================================
// Select Component
// ========================================

const Select = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  label,
  description,
  error,
  disabled = false,
  searchable = false,
  clearable = false,
  multiple = false,
  size = 'md',
  className = '',
  id: providedId,
  name,
  required = false,
  groupBy,
  renderOption,
  renderValue,
  maxHeight = 300,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const generatedId = useId();
  const id = providedId || generatedId;
  const listboxId = `${id}-listbox`;
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Flatten and filter options
  const flatOptions = options.flatMap(opt =>
    opt.options ? opt.options.map(o => ({ ...o, group: opt.label })) : [opt]
  );

  const filteredOptions = searchable && search
    ? flatOptions.filter(opt =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : flatOptions;

  // Group options if groupBy is provided
  const groupedOptions = groupBy
    ? filteredOptions.reduce((acc, opt) => {
        const group = opt[groupBy] || 'Other';
        if (!acc[group]) acc[group] = [];
        acc[group].push(opt);
        return acc;
      }, {})
    : null;

  // Get selected option(s)
  const getSelectedOption = (val) => flatOptions.find(opt => opt.value === val);

  const selectedOptions = multiple
    ? (value || []).map(getSelectedOption).filter(Boolean)
    : value ? [getSelectedOption(value)].filter(Boolean) : [];

  // Size styles
  const sizeStyles = {
    sm: {
      trigger: 'px-3 py-1.5 text-sm min-h-[32px]',
      option: 'px-3 py-1.5 text-sm',
      label: 'text-sm',
    },
    md: {
      trigger: 'px-3 py-2 text-sm min-h-[40px]',
      option: 'px-3 py-2 text-sm',
      label: 'text-sm',
    },
    lg: {
      trigger: 'px-4 py-2.5 text-base min-h-[48px]',
      option: 'px-4 py-2.5 text-base',
      label: 'text-base',
    },
  };

  const sizes = sizeStyles[size];

  // Event handlers
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearch('');
        setHighlightedIndex(0);
      }
    }
  };

  const handleSelect = (option) => {
    if (option.disabled) return;

    if (multiple) {
      const currentValues = value || [];
      const newValues = currentValues.includes(option.value)
        ? currentValues.filter(v => v !== option.value)
        : [...currentValues, option.value];
      onChange?.(newValues);
    } else {
      onChange?.(option.value);
      setIsOpen(false);
    }
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(multiple ? [] : null);
  };

  const handleRemoveTag = (e, val) => {
    e.stopPropagation();
    if (multiple) {
      onChange?.((value || []).filter(v => v !== val));
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(i =>
            i < filteredOptions.length - 1 ? i + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(i =>
            i > 0 ? i - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(filteredOptions.length - 1);
        }
        break;
      default:
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Render option content
  const renderOptionContent = (option) => {
    if (renderOption) return renderOption(option);
    return (
      <span className="flex items-center gap-2">
        {option.icon && <span className="w-4 h-4">{option.icon}</span>}
        <span>{option.label}</span>
        {option.description && (
          <span className="text-gray-400 text-xs ml-auto">{option.description}</span>
        )}
      </span>
    );
  };

  // Render selected value
  const renderSelectedValue = () => {
    if (selectedOptions.length === 0) {
      return <span className="text-gray-400">{placeholder}</span>;
    }

    if (renderValue) {
      return renderValue(selectedOptions);
    }

    if (multiple) {
      return (
        <div className="flex flex-wrap gap-1">
          {selectedOptions.map(opt => (
            <span
              key={opt.value}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-portfolio-purple/20
                       text-portfolio-purple rounded text-xs"
            >
              {opt.label}
              <button
                type="button"
                onClick={(e) => handleRemoveTag(e, opt.value)}
                className="hover:text-white focus:outline-none"
                aria-label={`Remove ${opt.label}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      );
    }

    return (
      <span className="flex items-center gap-2 text-white">
        {selectedOptions[0]?.icon && (
          <span className="w-4 h-4">{selectedOptions[0].icon}</span>
        )}
        {selectedOptions[0]?.label}
      </span>
    );
  };

  // Render dropdown list
  const renderOptions = () => {
    if (groupedOptions) {
      return Object.entries(groupedOptions).map(([group, opts]) => (
        <div key={group}>
          <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase">
            {group}
          </div>
          {opts.map((opt, idx) => renderOptionItem(opt, idx))}
        </div>
      ));
    }

    return filteredOptions.map((opt, idx) => renderOptionItem(opt, idx));
  };

  const renderOptionItem = (option, index) => {
    const isSelected = multiple
      ? (value || []).includes(option.value)
      : value === option.value;
    const isHighlighted = index === highlightedIndex;

    return (
      <div
        key={option.value}
        role="option"
        aria-selected={isSelected}
        data-highlighted={isHighlighted}
        onClick={() => handleSelect(option)}
        className={`
          ${sizes.option}
          cursor-pointer transition-colors
          ${isHighlighted ? 'bg-white/10' : ''}
          ${isSelected ? 'text-portfolio-purple' : 'text-white'}
          ${option.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'}
        `}
      >
        <div className="flex items-center gap-2">
          {multiple && (
            <span className={`
              w-4 h-4 rounded border flex items-center justify-center
              ${isSelected ? 'bg-portfolio-purple border-portfolio-purple' : 'border-white/30'}
            `}>
              {isSelected && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
          )}
          <span className="flex-1">{renderOptionContent(option)}</span>
          {!multiple && isSelected && (
            <svg className="w-4 h-4 text-portfolio-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      </div>
    );
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className={`block font-medium text-white mb-1.5 ${sizes.label}`}
        >
          {label}
          {required && <span className="text-error-400 ml-1">*</span>}
        </label>
      )}

      {/* Trigger */}
      <button
        ref={inputRef}
        type="button"
        id={id}
        name={name}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-describedby={[descriptionId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={`
          w-full ${sizes.trigger}
          bg-white/5 border rounded-lg
          text-left flex items-center gap-2
          focus:outline-none focus:ring-2 focus:ring-portfolio-purple
          focus:ring-offset-2 focus:ring-offset-gray-900
          transition-colors
          ${error ? 'border-error-500' : 'border-white/10 hover:border-white/20'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'border-portfolio-purple' : ''}
        `}
      >
        {/* Search input (when searchable) */}
        {searchable && isOpen ? (
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-white outline-none placeholder-gray-500"
            autoFocus
          />
        ) : (
          <span className="flex-1 truncate">{renderSelectedValue()}</span>
        )}

        {/* Clear button */}
        {clearable && selectedOptions.length > 0 && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-0.5 text-gray-400 hover:text-white rounded focus:outline-none"
            aria-label="Clear selection"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Description */}
      {description && (
        <p id={descriptionId} className="mt-1.5 text-sm text-gray-400">
          {description}
        </p>
      )}

      {/* Error */}
      {error && (
        <p id={errorId} className="mt-1.5 text-sm text-error-400">
          {error}
        </p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          id={listboxId}
          aria-multiselectable={multiple}
          className={`
            absolute z-50 w-full mt-1
            bg-surface-elevated border border-white/10 rounded-lg shadow-xl
            overflow-auto
          `}
          style={{ maxHeight }}
        >
          {filteredOptions.length > 0 ? (
            renderOptions()
          ) : (
            <div className="px-3 py-6 text-center text-gray-500">
              {search ? 'No results found' : 'No options available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

Select.propTypes = {
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      description: PropTypes.string,
      disabled: PropTypes.bool,
    }),
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })),
    }),
  ])),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.node,
  description: PropTypes.node,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  searchable: PropTypes.bool,
  clearable: PropTypes.bool,
  multiple: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
  id: PropTypes.string,
  name: PropTypes.string,
  required: PropTypes.bool,
  groupBy: PropTypes.string,
  renderOption: PropTypes.func,
  renderValue: PropTypes.func,
  maxHeight: PropTypes.number,
};

// ========================================
// Preset Selects
// ========================================

/**
 * LanguageSelect - For programming language selection
 */
export const LanguageSelect = ({ value, onChange, ...props }) => {
  const languages = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'ruby', label: 'Ruby' },
    { value: 'php', label: 'PHP' },
    { value: 'csharp', label: 'C#' },
    { value: 'cpp', label: 'C++' },
    { value: 'swift', label: 'Swift' },
    { value: 'kotlin', label: 'Kotlin' },
  ];

  return (
    <Select
      options={languages}
      value={value}
      onChange={onChange}
      placeholder="Select language..."
      searchable
      {...props}
    />
  );
};

LanguageSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

/**
 * LLMProviderSelect - For LLM provider selection
 */
export const LLMProviderSelect = ({ value, onChange, ...props }) => {
  const providers = [
    { value: 'zai', label: 'z.ai', description: 'Fast & cost-effective' },
    { value: 'inception', label: 'Inception', description: 'High quality' },
    { value: 'claude', label: 'Claude', description: 'Best reasoning' },
    { value: 'openai', label: 'OpenAI', description: 'GPT-4 compatible' },
  ];

  return (
    <Select
      options={providers}
      value={value}
      onChange={onChange}
      placeholder="Select provider..."
      {...props}
    />
  );
};

LLMProviderSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

/**
 * TimezoneSelect - For timezone selection
 */
export const TimezoneSelect = ({ value, onChange, ...props }) => {
  const timezones = [
    { label: 'Americas', options: [
      { value: 'America/New_York', label: 'Eastern Time (ET)' },
      { value: 'America/Chicago', label: 'Central Time (CT)' },
      { value: 'America/Denver', label: 'Mountain Time (MT)' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    ]},
    { label: 'Europe', options: [
      { value: 'Europe/London', label: 'London (GMT)' },
      { value: 'Europe/Paris', label: 'Paris (CET)' },
      { value: 'Europe/Berlin', label: 'Berlin (CET)' },
    ]},
    { label: 'Asia/Pacific', options: [
      { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
      { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
      { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
      { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
    ]},
  ];

  return (
    <Select
      options={timezones}
      value={value}
      onChange={onChange}
      placeholder="Select timezone..."
      searchable
      {...props}
    />
  );
};

TimezoneSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
};

export default Select;
